import { useState } from 'react';
import { useCallback } from 'react';

import { Search } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useInfiniteQuery } from 'react-query';
import { findOperationsSearch } from 'src/services/operation';
import { Operation } from 'src/types/operation';
import { debounce } from 'src/utils/general';

const PAGE_SIZE = 100;

type FundSearchProps = {
  onFundSelect: (fund: Operation) => void;
};

export function FundSearch({ onFundSelect }: FundSearchProps) {
  const [search, setSearch] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setSearch(value);
    }, 500),
    [],
  );

  const {
    data: fundsPages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingFunds,
  } = useInfiniteQuery(
    ['operations', search],
    ({ pageParam = 1 }) => findOperationsSearch(search, pageParam, PAGE_SIZE),
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
      enabled: inputFocused,
      retry: false,
      useErrorBoundary: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
    },
  );

  const operations = fundsPages
    ? fundsPages.pages.flatMap((page) => page.data)
    : [];

  return (
    <Autocomplete
      freeSolo
      blurOnSelect
      fullWidth
      popupIcon={null}
      noOptionsText={<Box>Nenhum fundo encontrado</Box>}
      loading={isLoadingFunds || isFetchingNextPage}
      loadingText="Carregando fundos..."
      options={Array.isArray(operations) ? operations : []}
      filterOptions={(options) => {
        return options;
      }}
      renderOption={(props, option) => (
        <Box {...props} component="li" sx={{ display: 'flex', gap: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {option.name}
          </Typography>
          -
          <Typography variant="body2" color="text.secondary">
            {option.cnpj}
          </Typography>
        </Box>
      )}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.name || '';
      }}
      onInputChange={(_, value) => {
        debouncedSetSearch(value);
      }}
      onChange={(_, value) => {
        if (value) {
          onFundSelect(value);
        }
      }}
      ListboxProps={{
        onScroll: (event) => {
          const listboxNode = event.currentTarget;
          if (
            listboxNode.scrollTop + listboxNode.clientHeight >=
            listboxNode.scrollHeight - 10
          ) {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Buscar fundos por nome ou CNPJ..."
          onFocus={() => {
            setInputFocused(true);
            if (!search) {
              setSearch('');
            }
          }}
          onBlur={() => setInputFocused(false)}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
}
